import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import type { ExtractionQuestion } from '@trinity/chat';
import { TagDetailsSection } from '../ProjectTags/TagDetails/TagDetailsSection';
import { TagDetailsSectionEditButtons } from '../ProjectTags/TagDetails/TagDetailsSectionEditButtons';
import { ExtractionQuestionRow } from './ExtractionQuestionRow';

export type ExtractionQuestionsFormProps = {
  items: ExtractionQuestion[];
  onSave?: (questions: ExtractionQuestion[]) => Promise<void>;
  isSaving?: boolean;
};

function createEmptyQuestion(): ExtractionQuestion {
  return {
    answerType: 'free_text' as const,
    questionText: '',
    label: '',
  };
}

export const ExtractionQuestionsForm = ({
  items,
  onSave,
  isSaving = false,
}: ExtractionQuestionsFormProps) => {
  const [editing, setEditing] = useState(false);
  const [questions, setQuestions] = useState<ExtractionQuestion[]>(items.length > 0 ? items : []);

  const handleChange = useCallback((index: number, updated: ExtractionQuestion) => {
    setQuestions((prev) => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  }, []);

  const handleAdd = useCallback(() => {
    setQuestions((prev) => [...prev, createEmptyQuestion()]);
  }, []);

  const handleRemove = useCallback((index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleCancel = useCallback(() => {
    setQuestions(items.length > 0 ? items : []);
    setEditing(false);
  }, [items]);

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    const valid = questions.filter((q) => q.questionText.trim() !== '' && q.label.trim() !== '');
    await onSave(valid);
    setQuestions(valid);
    setEditing(false);
  }, [questions, onSave]);

  const editActions = onSave ? (
    <TagDetailsSectionEditButtons
      isEditing={editing}
      isSaving={isSaving}
      onEdit={() => setEditing(true)}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  ) : undefined;

  return (
    <TagDetailsSection title="Structured Extraction Configuration" actions={editActions}>
      <Description>
        Configure questions to automatically extract specific data points. Each question defines what to
        extract and how the answer should be structured.
      </Description>

      {questions.length === 0 && !editing && (
        <EmptyState>No questions configured. Click Edit to add questions.</EmptyState>
      )}

      <QuestionsList>
        {questions.map((question, index) => (
          <ExtractionQuestionRow
            key={index}
            question={question}
            onChange={(updated) => handleChange(index, updated)}
            onRemove={() => handleRemove(index)}
            editing={editing}
          />
        ))}
      </QuestionsList>

      {editing && (
        <AddButton onClick={handleAdd} type="button">
          + Add Question
        </AddButton>
      )}
    </TagDetailsSection>
  );
};

ExtractionQuestionsForm.displayName = 'ExtractionQuestionsForm';

// ─── Styled ─────────────────────────────────────────────────────────────────

const Description = styled.div`
  font-size: 14px;
  padding: 15px 0px 10px;
  color: var(--gray-d, #555);
`;

const EmptyState = styled.div`
  font-size: 14px;
  color: var(--gray-d, #888);
  padding: 20px 0;
  text-align: center;
  font-style: italic;
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
`;

const AddButton = styled.button`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--pri-02);
  background: none;
  border: 1px dashed var(--pri-02);
  border-radius: 4px;
  cursor: pointer;
  padding: 10px 16px;
  margin-top: 12px;
  width: 100%;
  transition: background 0.15s ease;

  &:hover {
    background: var(--gray-xl, #f5f5f5);
  }
`;
